import { useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { max as d3Max } from 'd3-array'
import { formatAxisUsdCompact, niceAxisScale } from '@/components/gcp/gcpHelpers'
import { formatFixedAssetMoney, parseMoney } from './fixedAssetsFormat'
import {
  accumAreaPath,
  buildChartPoints,
  chartXScale,
  chartYScale,
  nearestPointIndex,
  nbvLinePath,
  todayX,
  type ScheduleMonthInput,
} from './fixedAssetChartGeometry'

const TOOLTIP_OFFSET = 12
const TOOLTIP_ESTIMATE_WIDTH = 200

type HoverState = {
  clientX: number
  clientY: number
  monthLabel: string
  expense: number
  accum: number
  nbv: number
}

function formatMonthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number)
  if (!year || !month) return key
  return new Date(year, month - 1, 1).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

interface FixedAssetBookValueChartProps {
  purchaseDate: string
  purchasePrice: string
  salvageValue: string
  months: ScheduleMonthInput[]
}

export default function FixedAssetBookValueChart({
  purchaseDate,
  purchasePrice,
  salvageValue,
  months,
}: FixedAssetBookValueChartProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<HoverState | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null)

  const purchaseNum = parseMoney(purchasePrice)
  const salvageNum = parseMoney(salvageValue)

  const points = useMemo(
    () =>
      buildChartPoints({
        purchaseDate,
        purchasePrice,
        salvage: salvageValue,
        months,
      }),
    [purchaseDate, purchasePrice, salvageValue, months],
  )

  const geometry = useMemo(() => {
    if (points.length === 0) {
      return null
    }

    const width = 520
    const height = 260
    const margin = { top: 16, right: 12, bottom: 44, left: 44 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom
    const maxNbv = d3Max(points, (point) => point.nbv) ?? 0
    const dataMax = Math.max(purchaseNum, maxNbv)
    const { max: yMax, ticks: yTicks } = niceAxisScale(dataMax, 5)
    const today = new Date()
    const todayLineX = todayX(points, today, innerWidth)
    const fullPath = nbvLinePath(points, innerWidth, innerHeight, yMax)
    const areaPath = accumAreaPath(points, purchaseNum, innerWidth, innerHeight, yMax)
    const purchaseY = chartYScale(purchaseNum, yMax, innerHeight)
    const salvageY =
      salvageNum > 0 ? chartYScale(salvageNum, yMax, innerHeight) : null

    let actualPath = ''
    let projectedPath = ''
    const firstProjectedIndex = points.findIndex((point) => point.isProjected)
    if (firstProjectedIndex === -1) {
      actualPath = fullPath
    } else if (firstProjectedIndex === 0) {
      projectedPath = fullPath
    } else {
      const actualPoints = points.slice(0, firstProjectedIndex)
      const projectedPoints = points.slice(firstProjectedIndex - 1)
      const pointCount = points.length
      actualPath = nbvLinePath(actualPoints, innerWidth, innerHeight, yMax, pointCount)
      projectedPath = nbvLinePath(projectedPoints, innerWidth, innerHeight, yMax, pointCount)
    }

    return {
      width,
      height,
      margin,
      innerWidth,
      innerHeight,
      yMax,
      yTicks,
      todayLineX,
      fullPath,
      areaPath,
      purchaseY,
      salvageY,
      actualPath,
      projectedPath,
      firstProjectedIndex,
    }
  }, [points, purchaseNum, salvageNum])

  useLayoutEffect(() => {
    if (!hover || !chartRef.current) {
      setTooltipPos(null)
      return
    }

    const rect = chartRef.current.getBoundingClientRect()
    const tooltipWidth = tooltipRef.current?.offsetWidth ?? TOOLTIP_ESTIMATE_WIDTH
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 88

    let left = hover.clientX - rect.left + TOOLTIP_OFFSET
    if (left + tooltipWidth > rect.width - 4) {
      left = hover.clientX - rect.left - tooltipWidth - TOOLTIP_OFFSET
    }
    left = Math.max(4, Math.min(left, rect.width - tooltipWidth - 4))

    let top = hover.clientY - rect.top + TOOLTIP_OFFSET
    top = Math.max(4, Math.min(top, rect.height - tooltipHeight - 4))

    setTooltipPos({ left, top })
  }, [hover])

  if (!geometry || points.length === 0) {
    return <p className="text-sm text-gray-500">No schedule data to chart.</p>
  }

  const {
    width,
    height,
    margin,
    innerWidth,
    innerHeight,
    yMax,
    yTicks,
    todayLineX,
    areaPath,
    purchaseY,
    salvageY,
    actualPath,
    projectedPath,
    firstProjectedIndex,
  } = geometry

  const handleMouseMove = (event: MouseEvent<SVGSVGElement>) => {
    const svg = event.currentTarget
    const rect = svg.getBoundingClientRect()
    const scaleX = width / rect.width
    const mouseX = (event.clientX - rect.left) * scaleX - margin.left
    const index = nearestPointIndex(points, innerWidth, mouseX)
    if (index < 0) return
    const point = points[index]
    if (!point) return
    setHover({
      clientX: event.clientX,
      clientY: event.clientY,
      monthLabel: formatMonthLabel(point.key),
      expense: point.expense,
      accum: point.accum,
      nbv: point.nbv,
    })
  }

  const xLabelIndices =
    points.length <= 8
      ? points.map((_, index) => index)
      : [0, Math.floor(points.length / 2), points.length - 1]

  return (
    <div ref={chartRef} className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full max-w-full"
        role="img"
        aria-label="Book value over time"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(null)}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {yTicks.map((value) => {
            const y = chartYScale(value, yMax, innerHeight)
            return (
              <g key={value}>
                <line x1={0} y1={y} x2={innerWidth} y2={y} stroke="#e5e7eb" />
                <text x={-8} y={y + 4} textAnchor="end" className="fill-gray-500 text-[10px]">
                  {formatAxisUsdCompact(value)}
                </text>
              </g>
            )
          })}

          <path d={areaPath} fill="#e5e7eb" opacity={0.55} />

          <line
            x1={0}
            y1={purchaseY}
            x2={innerWidth}
            y2={purchaseY}
            stroke="#9ca3af"
            strokeDasharray="4 4"
            strokeWidth={1}
          />

          {salvageY != null ? (
            <line
              x1={0}
              y1={salvageY}
              x2={innerWidth}
              y2={salvageY}
              stroke="#9ca3af"
              strokeDasharray="4 4"
              strokeWidth={1}
            />
          ) : null}

          {actualPath ? (
            <path
              d={actualPath}
              fill="none"
              stroke="#374151"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          ) : null}

          {projectedPath ? (
            <path
              d={projectedPath}
              fill="none"
              stroke="#374151"
              strokeWidth={2}
              strokeDasharray="5 4"
              opacity={0.65}
              strokeLinejoin="round"
            />
          ) : null}

          {todayLineX != null ? (
            <>
              <line
                x1={todayLineX}
                y1={0}
                x2={todayLineX}
                y2={innerHeight}
                stroke="#6b7280"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <text
                x={todayLineX}
                y={-4}
                textAnchor="middle"
                className="fill-gray-500 text-[9px]"
              >
                Today
              </text>
            </>
          ) : null}

          {xLabelIndices.map((index) => {
            const point = points[index]
            if (!point) return null
            const x = chartXScale(point.xIndex, points.length, innerWidth)
            return (
              <text
                key={point.key}
                x={x}
                y={innerHeight + 16}
                textAnchor="middle"
                className="fill-gray-600 text-[9px]"
              >
                {formatMonthLabel(point.key)}
              </text>
            )
          })}

          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
        </g>
      </svg>

      {hover ? (
        <div
          ref={tooltipRef}
          className="pointer-events-none absolute z-10 min-w-[11rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
          style={{
            left: tooltipPos?.left ?? hover.clientX,
            top: tooltipPos?.top ?? hover.clientY,
            visibility: tooltipPos ? 'visible' : 'hidden',
          }}
        >
          <p className="font-medium text-gray-900">{hover.monthLabel}</p>
          <p className="mt-1 text-gray-700">
            Expense: {formatFixedAssetMoney(hover.expense)}
          </p>
          <p className="text-gray-700">
            Accumulated: {formatFixedAssetMoney(hover.accum)}
          </p>
          <p className="text-gray-700">NBV: {formatFixedAssetMoney(hover.nbv)}</p>
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-gray-700" />
          Actual NBV
        </span>
        {firstProjectedIndex !== -1 ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 border-t border-dashed border-gray-600" />
            Projected
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 border-t border-dashed border-gray-400" />
          Purchase price
        </span>
        {salvageNum > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 border-t border-dashed border-gray-400" />
            Salvage value
          </span>
        ) : null}
      </div>
    </div>
  )
}
